import { applyMiddleware, combineReducers, compose, configureStore, createStore } from '@reduxjs/toolkit';
import { videoReducer } from 'pages/videos/video/store';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { createMigrate, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk from 'redux-thunk';
import { fbUploadReducer } from 'services/redux/fb-upload/reducer';
import { api, fbApi } from 'services/rtk';
import { translateApi } from 'services/rtk/google-translate/api';

import { authReducer } from './auth/slice';
import { fbPagesReducer } from './fb-pages/slice';
import { fbAccountsReducer } from './fbmanager/accounts/reducer';
import { cardReducer } from './fbmanager/cards/reducer';
import { fbCommentsReducer } from './fbmanager/comments/slice';
import { fbFiltersReducer } from './fbmanager/filters/reducer';
import { fbManagerReducer } from './fbmanager/reducer';
import { tablesSettingsReducer } from './fbmanager/table-settings/reducer';
import { migrations } from './migrations';
import { navigationReducer } from './navigation/reducer';
import { offersReducer } from './offers/reducer';
import { pagesReducer } from './pages/slice';
import { taskManagerReducer } from './task-manager/slice';
import { themeReducer } from './theme/reducer';
import { offersPageReducer } from '../../pages/affiliates/offers/offers/store';
import { tasksBoardReducer } from '../../pages/tasks-board/main/store';

export const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  [fbApi.reducerPath]: fbApi.reducer,
  [translateApi.reducerPath]: translateApi.reducer,
  auth: authReducer,
  cards: cardReducer,
  fbAccounts: fbAccountsReducer,
  fbcomments: fbCommentsReducer,
  fbFilters: fbFiltersReducer,
  fbmanager: fbManagerReducer,
  fbmanagerTablesSettings: tablesSettingsReducer,
  fbpages: fbPagesReducer,
  fbUpload: fbUploadReducer,
  navigation: navigationReducer,
  offers: offersReducer,
  offersPage: offersPageReducer,
  pages: pagesReducer,
  tasksBoard: tasksBoardReducer,
  taskManager: taskManagerReducer,
  theme: themeReducer,
  video: videoReducer,
});

const persistConfig = {
  key: 'root',
  storage,
  version: 10,
  migrate: createMigrate(migrations),
  whitelist: ['fbmanagerTablesSettings', 'fbpages', 'theme', 'navigation', 'auth', 'pages'],
};

export const persistedReducer = persistReducer(persistConfig, rootReducer);

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const enhancer = composeEnhancers(
  //@ts-expect-error
  applyMiddleware(thunk, api.middleware, fbApi.middleware, translateApi.middleware)
);

export default function setupStore() {
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(api.middleware, fbApi.middleware, translateApi.middleware),
  });
}

export const store = createStore(persistedReducer, enhancer);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof persistedReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export type AppThunk<ReturnType = void> = (dispatch: AppDispatch, getState: () => RootState) => ReturnType;
